/* ============================================================
   Trial card-capture / onboarding paywall — ported 1:1 from
   trial-pack/trial-01-card-capture.html. The .stripe-mount slot is where the
   Stripe Payment Element (Elements) mounts; OUR order-summary + CTA stay.
   This file is the VISUAL port + i18n; the live Stripe wiring (SetupIntent +
   PaymentElement + confirmSetup) and the onboarding gate are wired separately.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductVideoLink from '../components/ProductVideoLink.jsx';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import '../styles/trial-card-capture.css';
import { usePlanCatalog } from '../hooks/usePlanCatalog.js';
import { NEW_PLAN_DEFS, NEW_PLAN_TEXT_KEYS } from '../lib/plans.js';
import { buildPostingPriceLine, countConnectedSocials } from '../lib/postingPlan.js';
import { api } from '../services/api/client.js';

// Hardcoded fallback — used verbatim whenever the DB catalog fetch hasn't
// resolved yet, fails, or the brand_pilot row has no display price (never
// happens in practice today: migration 0080 backfills price_display_cents=
// 30000/price_display_was_cents=NULL for brand_pilot, i.e. parity with this
// exact constant). days/months (trial length + founding-promo duration) have
// NO server field yet (trial length is an env var server-side, not exposed
// via /api/billing/plans; promo duration is free text inside promo_then_text)
// — they always stay these hardcoded values (docs/TARIFF-REWORK-BRIEF.md §3/§4).
const N = { price1: '€150', price2: '€300', days: '7', months: '3' };

// NEW-grid fallback (docs/VYTIAG-tarify-core-video-2026-07-06.md §2): once
// newGridActive, the trial is framed as trial→Growth — plain €49/mo after the
// 7-day trial, NO founding discount. Used only when the live catalog's
// 'growth' row is missing/unresolved, exactly like N above for pilot.
const N_GROWTH = { price1: '€49', price2: '€49', days: '7', months: '3' };

// Resolve price1/price2 from the DB display catalog (price = priceDisplayCents
// "now" price, priceWas = priceDisplayWasCents "was" price) with a fallback to
// `fallback` (N for pilot, N_GROWTH for growth) for any missing/null field or
// a failed fetch.
function resolvePriceTokens(display, fallback = N) {
  return {
    price1: display?.priceFormatted || fallback.price1,
    price2: display?.priceWasFormatted || fallback.price2,
    days: fallback.days,
    months: fallback.months,
  };
}

function fill(s, tokens = N) {
  return String(s)
    .replace(/\{price1\}/g, tokens.price1)
    .replace(/\{price2\}/g, tokens.price2)
    .replace(/\{days\}/g, tokens.days)
    .replace(/\{months\}/g, tokens.months)
    // {plan} = the SELECTED tier's name (new grid). Absent from every legacy
    // COPY string, so this is a no-op for the legacy path (byte-identical).
    .replace(/\{plan\}/g, tokens.plan ?? '');
}

const COPY = {
  en: {
    eyebrow: '{days}-day free trial',
    headline: 'Start creating today — <em>free for {days} days.</em>',
    lede: 'Add your card and we get to work right away — researching your market, building your content strategy, and drafting your first post.',
    trust1: '100% no-risk free trial', trust2: 'Pay nothing for the first {days} days', trust3: 'Cancel anytime, from Settings',
    inclTitle: 'Everything in Brand Pilot',
    f1: 'Multi-channel <b>scheduling</b>', f2: 'AI <b>content + images</b>', f3: 'Deep <b>market research</b>',
    f4: '<b>Autopilot</b> posting', f5: '<b>1 brand</b> workspace', f6: 'Reusable <b>brand library</b>',
    proof: '<b>Built for solo brands & studios</b> — set up once, ship every week.',
    planName: 'Brand Pilot', founding: 'Founding · 50% off',
    dueToday: 'due<br/><b>today</b>',
    then: 'Then <b>{price1}/mo</b> for your first {months} months <span class="strike">{price2}</span>, then <b>{price2}/mo</b>. Cancel anytime.',
    payTitle: 'Payment', securedBy: 'Secured by Stripe',
    cta: 'Start free trial — €0 today',
    secureLine: "256-bit encryption · you won't be charged today",
    fine: 'By starting your trial you authorize Dathent to charge your card when it ends, unless you cancel. Cancel anytime in Settings. <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a> · <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>.',
    q1: 'Will I be charged today?', a1: 'No — you pay <b>€0 today</b>. Your card is kept on file so billing can begin automatically when the {days}-day trial ends, only if you haven\'t cancelled.',
    q2: 'What happens after the trial?', a2: '<b>Brand Pilot</b> at the founding rate — <b>{price1}/month for {months} months</b> (50% off), then <b>{price2}/month</b>. Cancel anytime in Settings.',
    footer: 'Your {days}-day trial is <b>100% free</b> — cancel anytime from Settings.',
    loading: 'Loading secure payment…',
  },
  uk: {
    eyebrow: '{days} днів безкоштовно',
    headline: 'Почніть творити сьогодні — <em>{days} днів безкоштовно.</em>',
    lede: 'Додайте картку — і ми одразу беремося до роботи: досліджуємо ваш ринок, будуємо контент-стратегію та готуємо ваш перший пост.',
    trust1: '100% без ризику', trust2: 'Платіть €0 перші {days} днів', trust3: 'Скасування будь-коли в Налаштуваннях',
    inclTitle: 'Усе у Brand Pilot',
    f1: 'Багатоканальне <b>планування</b>', f2: 'AI <b>контент + зображення</b>', f3: 'Глибоке <b>дослідження ринку</b>',
    f4: '<b>Автопілот</b> — пости', f5: '<b>1 бренд</b> — простір', f6: 'Багаторазова <b>бренд-бібліотека</b>',
    proof: '<b>Для соло-брендів і студій</b> — налаштуйте один раз, публікуйте щотижня.',
    planName: 'Brand Pilot', founding: 'Стартова · −50%',
    dueToday: 'до сплати<br/><b>сьогодні</b>',
    then: 'Далі <b>{price1}/міс</b> перші {months} місяці <span class="strike">{price2}</span>, потім <b>{price2}/міс</b>. Скасування будь-коли.',
    payTitle: 'Оплата', securedBy: 'Захищено Stripe',
    cta: 'Почати безкоштовно — €0 сьогодні',
    secureLine: '256-бітне шифрування · сьогодні без списань',
    fine: 'Починаючи пробний період, ви дозволяєте Dathent списати кошти після його завершення, якщо не скасуєте. Скасування будь-коли в Налаштуваннях. <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">Умови</a> · <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">Конфіденційність</a>.',
    q1: 'Чи спишуть кошти сьогодні?', a1: 'Ні — сьогодні ви платите <b>€0</b>. Картка зберігається, щоб оплата почалася автоматично після {days}-денного періоду, лише якщо ви не скасували.',
    q2: 'Що буде після пробного періоду?', a2: '<b>Brand Pilot</b> за стартовою ціною — <b>{price1}/міс перші {months} місяці</b> (−50%), далі <b>{price2}/міс</b>. Скасування будь-коли.',
    footer: 'Ваш {days}-денний пробний період <b>повністю безкоштовний</b> — скасуйте будь-коли в Налаштуваннях.',
    loading: 'Завантаження захищеної оплати…',
  },
  ru: {
    eyebrow: '{days} дней бесплатно',
    headline: 'Начните создавать сегодня — <em>{days} дней бесплатно.</em>',
    lede: 'Добавьте карту — и мы сразу беремся за работу: исследуем ваш рынок, строим контент-стратегию и готовим ваш первый пост.',
    trust1: '100% без риска', trust2: 'Платите €0 первые {days} дней', trust3: 'Отмена в любой момент в Настройках',
    inclTitle: 'Всё в Brand Pilot',
    f1: 'Многоканальное <b>планирование</b>', f2: 'AI <b>контент + изображения</b>', f3: 'Глубокое <b>исследование рынка</b>',
    f4: '<b>Автопилот</b> — посты', f5: '<b>1 бренд</b> — пространство', f6: 'Многоразовая <b>бренд-библиотека</b>',
    proof: '<b>Для соло-брендов и студий</b> — настройте один раз, публикуйте каждую неделю.',
    planName: 'Brand Pilot', founding: 'Стартовая · −50%',
    dueToday: 'к оплате<br/><b>сегодня</b>',
    then: 'Затем <b>{price1}/мес</b> первые {months} месяца <span class="strike">{price2}</span>, потом <b>{price2}/мес</b>. Отмена в любой момент.',
    payTitle: 'Оплата', securedBy: 'Защищено Stripe',
    cta: 'Начать бесплатно — €0 сегодня',
    secureLine: '256-битное шифрование · сегодня без списаний',
    fine: 'Начиная пробный период, вы разрешаете Dathent списать средства после его окончания, если не отмените. Отмена в любой момент в Настройках. <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">Условия</a> · <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">Конфиденциальность</a>.',
    q1: 'Спишут ли деньги сегодня?', a1: 'Нет — сегодня вы платите <b>€0</b>. Карта сохраняется, чтобы оплата началась автоматически после {days}-дневного периода, только если вы не отменили.',
    q2: 'Что будет после пробного периода?', a2: '<b>Brand Pilot</b> по стартовой цене — <b>{price1}/мес первые {months} месяца</b> (−50%), затем <b>{price2}/мес</b>. Отмена в любой момент.',
    footer: 'Ваш {days}-дневный пробный период <b>полностью бесплатный</b> — отмените в любой момент в Настройках.',
    loading: 'Загрузка защищённой оплаты…',
  },
};

// NEW-grid copy (docs/VYTIAG-tarify-core-video-2026-07-06.md §2): trial→Growth
// instead of trial→Brand Pilot, used only once newGridActive. Mirrors COPY's
// shape key-for-key; fields that don't mention the plan by name (headline,
// lede, trust*, payTitle/securedBy, cta, secureLine, fine, q1/a1, footer,
// loading) are identical wording to COPY on purpose — the plan-specific
// fields (inclTitle, f1–f6, proof, planName, q2/a2) change, and so do
// `founding`/`then`: the owner removed the founding −50% discount for the new
// grid entirely (2026-07-07) — Growth converts to a PLAIN €49/mo after the
// 7-day trial, so `founding` is '' (no badge rendered — see the conditional
// render below) and `then` states the flat post-trial price with no strike-
// through / "first N months" framing. The legacy COPY.founding/then above
// (Brand Pilot's real 50%-off-first-3-months rate) is untouched.
const COPY_GROWTH = {
  en: {
    eyebrow: '{days}-day free trial',
    headline: 'Choose your plan — <em>free for {days} days.</em>',
    lede: "Pick the plan to start on. You won't be charged today — cancel anytime.",
    trust1: '100% no-risk free trial', trust2: 'Pay nothing for the first {days} days', trust3: 'Cancel anytime, from Settings',
    inclTitle: 'Everything in {plan}',
    f1: 'Up to <b>6 channels</b>', f2: 'AI <b>content + images</b>', f3: '<b>Autopilot</b> posting',
    f4: '<b>Deep research</b> — market & competitors', f5: '<b>Smart insights</b> — feedback loop on every post', f6: '<b>Video</b> slideshows — Reels & Shorts',
    proof: '<b>Your SMM department on autopilot</b> — set up once, ship every week.',
    planName: 'Growth', founding: '',
    dueToday: 'due<br/><b>today</b>',
    then: 'Then <b>{price1}/mo</b> after your {days}-day trial. Cancel anytime.',
    payTitle: 'Payment', securedBy: 'Secured by Stripe',
    cta: 'Start free trial — €0 today',
    secureLine: "256-bit encryption · you won't be charged today",
    fine: 'By starting your trial you authorize Dathent to charge your card when it ends, unless you cancel. Cancel anytime in Settings. <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a> · <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>.',
    q1: 'Will I be charged today?', a1: 'No — you pay <b>€0 today</b>. Your card is kept on file so billing can begin automatically when the {days}-day trial ends, only if you haven\'t cancelled.',
    q2: 'What happens after the trial?', a2: '<b>{plan}</b> at <b>{price1}/month</b> after your {days}-day free trial. Cancel anytime in Settings.',
    footer: 'Your {days}-day trial is <b>100% free</b> — cancel anytime from Settings.',
    loading: 'Loading secure payment…',
  },
  uk: {
    eyebrow: '{days} днів безкоштовно',
    headline: 'Оберіть план — <em>{days} днів безкоштовно.</em>',
    lede: 'Оберіть план, з якого почати. Сьогодні без списань — скасування будь-коли.',
    trust1: '100% без ризику', trust2: 'Платіть €0 перші {days} днів', trust3: 'Скасування будь-коли в Налаштуваннях',
    inclTitle: 'Усе у {plan}',
    f1: 'До <b>6 каналів</b>', f2: 'AI <b>контент + зображення</b>', f3: '<b>Автопілот</b> — пости',
    f4: '<b>Глибоке дослідження</b> ринку й конкурентів', f5: '<b>Розумні інсайти</b> — фідбек по кожному посту', f6: '<b>Відео</b>-слайдшоу — Reels та Shorts',
    proof: '<b>Ваш SMM-відділ на автопілоті</b> — налаштуйте один раз, публікуйте щотижня.',
    planName: 'Growth', founding: '',
    dueToday: 'до сплати<br/><b>сьогодні</b>',
    then: 'Далі <b>{price1}/міс</b> після {days}-денного пробного періоду. Скасування будь-коли.',
    payTitle: 'Оплата', securedBy: 'Захищено Stripe',
    cta: 'Почати безкоштовно — €0 сьогодні',
    secureLine: '256-бітне шифрування · сьогодні без списань',
    fine: 'Починаючи пробний період, ви дозволяєте Dathent списати кошти після його завершення, якщо не скасуєте. Скасування будь-коли в Налаштуваннях. <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">Умови</a> · <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">Конфіденційність</a>.',
    q1: 'Чи спишуть кошти сьогодні?', a1: 'Ні — сьогодні ви платите <b>€0</b>. Картка зберігається, щоб оплата почалася автоматично після {days}-денного періоду, лише якщо ви не скасували.',
    q2: 'Що буде після пробного періоду?', a2: '<b>{plan}</b> за <b>{price1}/міс</b> після {days}-денного безкоштовного періоду. Скасування будь-коли.',
    footer: 'Ваш {days}-денний пробний період <b>повністю безкоштовний</b> — скасуйте будь-коли в Налаштуваннях.',
    loading: 'Завантаження захищеної оплати…',
  },
  ru: {
    eyebrow: '{days} дней бесплатно',
    headline: 'Выберите план — <em>{days} дней бесплатно.</em>',
    lede: 'Выберите план, с которого начать. Сегодня без списаний — отмена в любой момент.',
    trust1: '100% без риска', trust2: 'Платите €0 первые {days} дней', trust3: 'Отмена в любой момент в Настройках',
    inclTitle: 'Всё в {plan}',
    f1: 'До <b>6 каналов</b>', f2: 'AI <b>контент + изображения</b>', f3: '<b>Автопилот</b> — посты',
    f4: '<b>Глубокое исследование</b> рынка и конкурентов', f5: '<b>Умные инсайты</b> — фидбек по каждому посту', f6: '<b>Видео</b>-слайдшоу — Reels и Shorts',
    proof: '<b>Ваш SMM-отдел на автопилоте</b> — настройте один раз, публикуйте каждую неделю.',
    planName: 'Growth', founding: '',
    dueToday: 'к оплате<br/><b>сегодня</b>',
    then: 'Затем <b>{price1}/мес</b> после {days}-дневного пробного периода. Отмена в любой момент.',
    payTitle: 'Оплата', securedBy: 'Защищено Stripe',
    cta: 'Начать бесплатно — €0 сегодня',
    secureLine: '256-битное шифрование · сегодня без списаний',
    fine: 'Начиная пробный период, вы разрешаете Dathent списать средства после его окончания, если не отмените. Отмена в любой момент в Настройках. <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">Условия</a> · <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">Конфиденциальность</a>.',
    q1: 'Спишут ли деньги сегодня?', a1: 'Нет — сегодня вы платите <b>€0</b>. Карта сохраняется, чтобы оплата началась автоматически после {days}-дневного периода, только если вы не отменили.',
    q2: 'Что будет после пробного периода?', a2: '<b>{plan}</b> по <b>{price1}/мес</b> после {days}-дневного бесплатного периода. Отмена в любой момент.',
    footer: 'Ваш {days}-дневный пробный период <b>полностью бесплатный</b> — отмените в любой момент в Настройках.',
    loading: 'Загрузка защищённой оплаты…',
  },
};

const CK = (sz = 13) => (
  <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
);

/**
 * @param {object}   props
 * @param {React.ReactNode} [props.stripeSlot]  the live Stripe Payment Element (when wired). When
 *   absent, a loading skeleton is shown. The order-summary + CTA are always ours.
 * @param {boolean}  [props.submitting]
 * @param {boolean}  [props.canSubmit]
 * @param {string}   [props.error]
 * @param {() => void} [props.onSubmit]
 * @param {string}   [props.selectedPlanKey]  new-grid picker: the chosen tier id
 *   ('starter'|'growth'|'pro'|'agency'). Ignored on the legacy (pre-flip) path.
 * @param {(planKey: string) => void} [props.onSelectPlan]  new-grid picker: called
 *   when the user picks a tier. When absent the strip renders read-only.
 */
export function TrialCardCapture({ stripeSlot = null, submitting = false, controlsLocked = false, canSubmit = false, error = null, onSubmit, selectedPlanKey = 'growth', onSelectPlan, chatGptOrigin = false, automaticTax = null, taxQuote = null }) {
  const { t, i18n } = useTranslation();
  const taxSummary = taxQuote
    ? t('trial.nativeTaxTotal', { defaultValue: 'First payment after the trial: {{total}}, including {{tax}} tax.', total: new Intl.NumberFormat(i18n.language, { style: 'currency', currency: taxQuote.currency }).format(taxQuote.total / 100), tax: new Intl.NumberFormat(i18n.language, { style: 'currency', currency: taxQuote.currency }).format(taxQuote.tax / 100) })
    : t('trial.nativeTaxPending', 'Your total after the trial updates from your Stripe billing details.');
  const { getPlanDisplay, getNewPlanDisplay, getPostingPlanDisplay, newGridActive, postingPlanActive, ready } = usePlanCatalog();
  const navigate = useNavigate();
  const { logout } = useAuth();
  // Escape hatch — the card gate replaces the whole app (no sidebar/logout), so
  // give a way out (wrong account / changed mind / shared device). logout()
  // clears the session; RequireAuth then bounces to /login (navigate is a belt).
  const handleSignOut = async () => {
    try { await logout(); } finally { navigate('/login', { replace: true }); }
  };
  const lang = ['en', 'uk', 'ru'].includes((i18n.language || 'en').slice(0, 2)) ? i18n.language.slice(0, 2) : 'en';
  // "Compare full details" in-place disclosure (§4.4) — never routes to
  // /app/plans (which bounces through RequireActiveBilling while gated).
  const [compareOpen, setCompareOpen] = useState(false);
  const [connectedSocials, setConnectedSocials] = useState([]);
  // Once the NEW tariff grid is live, trial framing switches from Brand Pilot
  // to Growth (docs/VYTIAG-tarify-core-video-2026-07-06.md §2 — Trial: 7 days
  // with card → Starter-level limits, but the subscribed/billed plan is
  // Growth). COPY_GROWTH mirrors COPY key-for-key so nothing else below needs
  // to branch.
  //
  // TORN-RENDER GUARD: the copy-source choice is LATCHED (in a ref) on the
  // first render where the catalog is READY, and never changes afterwards —
  // the money copy can't flip mid-read once settled. Before the catalog
  // resolves, newGridActive is false by construction (null cache map), so
  // pre-ready frames render the legacy copy; the single settle at `ready`
  // happens within one catalog-fetch RTT, while the Stripe skeleton is still
  // up. Deliberately latched at READY rather than at literal first paint: a
  // first-paint latch on a cold-cache page post-flip would permanently pin
  // Brand-Pilot copy over a trial that actually subscribes Growth — a worse
  // (money-copy) mismatch than one early settle. Pre-flip both strategies are
  // identical: newGridActive is false before AND at ready, so the render is
  // byte-identical to the old hardcoded path and never flips.
  const gridChoiceRef = useRef(null); // null = not latched yet
  if (gridChoiceRef.current === null && ready) gridChoiceRef.current = !!newGridActive;
  const useNewGrid = gridChoiceRef.current !== null ? gridChoiceRef.current : !!newGridActive;
  const copySource = useNewGrid ? COPY_GROWTH : COPY;
  const tr = copySource[lang] || copySource.en;
  // DB-backed display catalog (docs/TARIFF-REWORK-BRIEF.md §3/§4): resolves
  // the live price/priceWas from the server (brand_pilot pre-flip, growth once
  // the latched choice is the new grid), falling back to the matching
  // hardcoded N/N_GROWTH constant for any missing field or failed fetch.
  // Derived from the SAME latched boolean as the copy, so price tokens and
  // the surrounding words can never tear apart within one render.
  // NEW-GRID tier picker (docs/onboarding-tariff-select-design-brief.md §4):
  // the selected tier drives the price shown + the plan the trial converts to.
  // Falls back to 'growth' (the featured/recommended default) if an unknown key
  // ever arrives. Localizes plan display via NEW_PLAN_TEXT_KEYS (t('settings.'+
  // key), English NEW_PLAN_DEFS as the fallback) — the SAME source Plans.jsx
  // uses, so names/feats can't drift between the two surfaces.
  const tSet = (k, fb) => (k ? t(`settings.${k}`, { defaultValue: fb }) : fb);
  useEffect(() => {
    if (!useNewGrid || !postingPlanActive) return;
    let cancelled = false;
    api.socialAccounts.list()
      .then((response) => { if (!cancelled) setConnectedSocials(response?.accounts || response?.data?.accounts || []); })
      .catch(() => { if (!cancelled) setConnectedSocials([]); });
    return () => { cancelled = true; };
  }, [useNewGrid, postingPlanActive]);

  const connectedCount = countConnectedSocials(connectedSocials);
  const postingDisplay = getPostingPlanDisplay();
  const postingLine = buildPostingPriceLine({
    quantity: Math.max(1, connectedCount),
    unitPriceCents: postingDisplay.priceCents ?? undefined,
    currency: postingDisplay.currency,
  });
  // Per-row strip data (all 4 tiers). Prices via getNewPlanDisplay so an admin
  // DB price edit propagates; hardcoded NEW_PLAN_DEFS price as the fallback.
  const regularStripRows = NEW_PLAN_DEFS.map((p) => {
    const k = NEW_PLAN_TEXT_KEYS[p.id] || {};
    const d = getNewPlanDisplay(p.id);
    return {
      id: p.id,
      featured: !!p.featured,
      name: tSet(k.name, p.name),
      best: tSet(k.desc, p.desc),
      price: d.priceFormatted || p.price,
      unit: tSet(k.priceUnit, p.priceUnit),
      feats: [t('postingPlan.higherTierContrast'), ...(p.feats || []).map((f, i) => tSet(k.feats?.[i], f))],
    };
  });
  const postingRow = postingPlanActive ? {
    id: 'posting',
    featured: false,
    name: t('postingGate.name'),
    best: t('postingPlan.description'),
    price: postingLine.unitFormatted,
    unit: t('postingGate.unit'),
    included: [1, 2, 3].map((i) => t(`postingGate.included${i}`)),
    feats: [1, 2, 3].map((i) => t(`postingGate.included${i}`)),
  } : null;
  const stripRows = postingRow
    ? (chatGptOrigin ? [postingRow, ...regularStripRows] : [...regularStripRows, postingRow])
    : regularStripRows;
  const selKey = useNewGrid && stripRows.some((p) => p.id === selectedPlanKey) ? selectedPlanKey : 'growth';
  const selDef = NEW_PLAN_DEFS.find((p) => p.id === selKey) || null;
  const selKeys = NEW_PLAN_TEXT_KEYS[selKey] || {};
  const selName = selKey === 'posting' ? postingRow.name : tSet(selKeys.name, selDef?.name || 'Growth');
  const postingSelected = selKey === 'posting';
  const selFeats = stripRows.find((r) => r.id === selKey)?.feats || [];

  // Price tokens follow the SELECTED tier (new grid) — so the right panel's
  // `then` price and the FAQ price move with the picker without any Stripe
  // remount. Legacy path: pilot, unchanged.
  const display = useNewGrid && !postingSelected ? getNewPlanDisplay(selKey) : getPlanDisplay('pilot');
  const tokens = resolvePriceTokens(display, useNewGrid ? N_GROWTH : N);
  // {plan} token = the selected tier's localized name (new grid). Legacy COPY
  // strings never contain {plan}, so this stays inert for the legacy path.
  tokens.plan = useNewGrid ? selName : (tr.planName || '');
  const H = (k) => ({ dangerouslySetInnerHTML: { __html: fill(tr[k], tokens) } });

  // §7.1: localized legal pages exist on the landing only as /ru/ and — note
  // the folder name — /ua/ (NOT /uk/). Rewrite the Terms/Privacy hrefs by lang.
  // NEW-GRID ONLY — the legacy COPY.fine is left byte-identical to prod.
  const fineHtml = (() => {
    const html = fill(tr.fine, tokens);
    if (!useNewGrid) return html;
    if (lang === 'uk') return html.replace(/https:\/\/dathent\.ai\/(terms|privacy)/g, 'https://dathent.ai/ua/$1');
    if (lang === 'ru') return html.replace(/https:\/\/dathent\.ai\/(terms|privacy)/g, 'https://dathent.ai/ru/$1');
    return html;
  })();

  const submit = (e) => { e.preventDefault(); if (canSubmit && !submitting) onSubmit?.(); };

  return (
    <div className="tcc-page">
      <button type="button" className="tcc-signout" onClick={handleSignOut}>
        {useNewGrid ? t('postingGate.signOut') : t('trial.notYouSignOut', { defaultValue: 'Not you? Log out' })}
      </button>
      <div className="tcc-wrap">
        <div className="tcc-shell">

          {/* LEFT — persuasion */}
          <div className="tcc-left">
            <div className="tcc-brand">
              <img src="/assets/dathent-logo.png" alt="Dathent" />
              <div className="wm">Dathent</div>
            </div>

            <div className="tcc-eyebrow">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2L12 16.6 5.7 21l2.3-7.2-6-4.4h7.6z" /></svg>
              <span>{postingSelected ? t('postingGate.eyebrow') : fill(tr.eyebrow)}</span>
            </div>
            {postingSelected
              ? <h1 className="tcc-h">{t('postingGate.title')}</h1>
              : <h1 className="tcc-h" {...H('headline')} />}
            <p className="tcc-lede">{postingSelected ? t('postingGate.subtitle') : fill(tr.lede)}</p>
            <ProductVideoLink />

            {!useNewGrid && (
              <div className="tcc-trust">
                <div className="row"><span className="ck">{CK()}</span><span>{fill(tr.trust1)}</span></div>
                <div className="row"><span className="ck">{CK()}</span><span>{fill(tr.trust2)}</span></div>
                <div className="row"><span className="ck">{CK()}</span><span>{fill(tr.trust3)}</span></div>
              </div>
            )}

            {useNewGrid ? (
              <div className="tcc-picker">
                {/* PLAN STRIP — 4 selectable self-serve tiers (radiogroup) */}
                <div className="tcc-strip" role="radiogroup" aria-label={fill(tr.inclTitle)}>
                  {stripRows.map((r) => {
                    const sel = r.id === selKey;
                    return (
                      <button
                        type="button"
                        key={r.id}
                        role="radio"
                        aria-checked={sel}
                        className={`plan-row${sel ? ' sel' : ''}${r.featured ? ' featured' : ''}`}
                        disabled={submitting || controlsLocked}
                        onClick={() => { if (!submitting && !controlsLocked) onSelectPlan?.(r.id); }}
                      >
                        <span className="plan-radio" aria-hidden="true" />
                        <span className="plan-info">
                          <span className="plan-nm">
                            {r.name}
                            {r.featured && <span className="plan-rec">{t('postingGate.recommended')}</span>}
                          </span>
                          <span className="plan-best">{r.best}</span>
                        </span>
                        <span className="plan-cost"><b className="p">{r.price}</b><span className="u">{r.unit}</span></span>
                      </button>
                    );
                  })}
                </div>

                {/* Selected tier's full features. Posting uses the canonical brief facts. */}
                <div className="tcc-incl tcc-incl-sel">
                  {postingSelected ? (
                    <>
                      <p className="ttl">{connectedCount === 0
                        ? t('postingGate.quantityZero')
                        : t('postingGate.quantity', { unit: postingLine.unitFormatted, count: connectedCount, total: postingLine.totalFormatted })}</p>
                      <p className="ttl">{t('postingGate.minimum')}</p>
                       <ul className="tcc-feats">{postingRow.included.map((f) => <li key={f}><span className="ck">{CK(12)}</span><span>{f}</span></li>)}</ul>
                       <p className="ttl muted">{t('postingPlan.noAiNote')}</p>
                    </>
                  ) : (
                    <>
                      <p className="ttl">{fill(tr.inclTitle, tokens)}</p>
                      <ul className="tcc-feats">
                        {selFeats.map((f, i) => (
                          <li key={i}><span className="ck">{CK(12)}</span><span>{f}</span></li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                {/* Compare full details — in-place disclosure, never routes */}
                <button
                  type="button"
                  className="tcc-compare-toggle"
                  aria-expanded={compareOpen}
                  onClick={() => setCompareOpen((v) => !v)}
                >
                  {t('postingGate.compare')}
                  <svg className={`chev${compareOpen ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                {compareOpen && (
                  <div className="tcc-compare">
                    {stripRows.map((r) => {
                      const sel = r.id === selKey;
                      return (
                        <div key={r.id} className={`cmp-tier${sel ? ' sel' : ''}`}>
                          <div className="cmp-head">
                            <span className="cmp-nm">{r.name}{r.featured && <span className="plan-rec">{t('postingGate.recommended')}</span>}</span>
                            <span className="cmp-price"><b>{r.price}</b><span className="u">{r.unit}</span></span>
                          </div>
                          <ul className="cmp-feats">
                            {r.feats.map((f, i) => (
                              <li key={i}><span className="ck">{CK(11)}</span><span>{f}</span></li>
                            ))}
                          </ul>
                          {sel ? (
                            <span className="cmp-cur">{t('postingGate.selected')}</span>
                          ) : (
                            <button
                              type="button"
                              className="cmp-choose"
                              disabled={submitting || controlsLocked}
                              onClick={() => { if (!submitting && !controlsLocked) { onSelectPlan?.(r.id); setCompareOpen(false); } }}
                            >
                              {r.id === 'posting'
                                ? t('postingGate.postingSelect')
                                : t('plansPage.chooseX', { plan: r.name, defaultValue: `Choose ${r.name}` })}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="tcc-incl">
                <p className="ttl">{fill(tr.inclTitle)}</p>
                <ul className="tcc-feats">
                  {['f1', 'f2', 'f3', 'f4', 'f5', 'f6'].map((k) => (
                    <li key={k}><span className="ck">{CK(12)}</span><span {...H(k)} /></li>
                  ))}
                </ul>
              </div>
            )}

            {!postingSelected && <div className="tcc-proof">
              <div className="tcc-avs"><div className="av">P</div><div className="av">V</div><div className="av">A</div></div>
              <p {...H('proof')} />
            </div>}

            {!useNewGrid && (
              <div className="tcc-faq">
                <details open>
                  <summary><span>{fill(tr.q1)}</span><span className="pl"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14" /></svg></span></summary>
                  <div className="ans" {...H('a1')} />
                </details>
                <details>
                  <summary><span>{fill(tr.q2)}</span><span className="pl"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14" /></svg></span></summary>
                  <div className="ans">{automaticTax ? taxSummary : <span {...H('a2')} />}</div>
                </details>
              </div>
            )}
          </div>

          {/* RIGHT — checkout panel (our summary + CTA; Stripe renders only the card fields) */}
          <aside className="tcc-pay" aria-label="Checkout">
            <form className="tcc-panel" onSubmit={submit}>
              <div className="tcc-plan-top">
                <div className="tcc-plan-row">
                  <span className="nm">{useNewGrid ? selName : fill(tr.planName)}</span>
                  {tr.founding && <span className="tcc-founding">{fill(tr.founding)}</span>}
                </div>
                {postingSelected ? (
                  <>
                    <div className="tcc-price"><span className="big">{postingLine.totalFormatted}</span></div>
                    <p className="tcc-then">{connectedCount === 0
                      ? t('postingGate.quantityZero')
                      : t('postingGate.quantity', { unit: postingLine.unitFormatted, count: connectedCount, total: postingLine.totalFormatted })}</p>
                    <p className="tcc-limitnote">{t('postingGate.minimum')}</p>
                  </>
                ) : (
                  <>
                    <div className="tcc-price">
                      <span className="big">€0</span>
                      <span className="today" {...H('dueToday')} />
                    </div>
                    {automaticTax ? <p className="tcc-then" aria-live="polite">{taxSummary}</p> : <p className="tcc-then" {...H('then')} />}
                  </>
                )}
                {/* Honest trial-limits line (§2.2 / §4.5): free-tier limits until
                    the first payment, unlocking to the SELECTED plan. Reuses the
                    existing settings.trialLimitsNote key ({{plan}}), all 10 locales. */}
                {useNewGrid && !postingSelected && (
                  <p className="tcc-limitnote">{t('settings.trialLimitsNote', { plan: selName })}</p>
                )}
              </div>

              <div className="tcc-embed">
                <div className="embed-head">
                  <span className="lbl">{postingSelected ? t('postingGate.cardTitle') : fill(tr.payTitle)}</span>
                  <span className="by"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg><span>{postingSelected ? t('postingGate.secure') : fill(tr.securedBy)}</span></span>
                </div>

                {/* Stripe Payment Element mounts into .stripe-mount when wired (stripeSlot) */}
                <div className={`stripe-mount${stripeSlot ? ' ready' : ''}`} id="stripe-payment-element">
                  {stripeSlot
                    ? <div className="form">{stripeSlot}</div>
                    : (
                      <div className="skel" aria-label={fill(tr.loading)}>
                        <div className="sb" style={{ height: 52 }} />
                        <div className="sb" />
                        <div className="sb row"><span /><span /></div>
                        <div className="sb" />
                      </div>
                    )}
                  <span className="pwr">Powered by <b>stripe</b></span>
                </div>

                <button type="submit" className="tcc-cta" disabled={!canSubmit || submitting}>
                   <span>{postingSelected ? t(submitting ? 'postingGate.submitting' : 'postingGate.submit') : (submitting ? fill(tr.loading) : fill(tr.cta))}</span>
                  <svg className="arr" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </button>
                <div className="tcc-secure">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                  <span>{postingSelected ? t('postingGate.secure') : fill(tr.secureLine)}</span>
                </div>
                {error && <div className="tcc-err">{error}</div>}
              </div>

              {!postingSelected && <div className="tcc-fine">
                {automaticTax ? <p>{t('trial.taxTrialTerms', 'Your trial is free. By starting it, you agree to the reviewed first payment after the trial and recurring billing thereafter. Cancel anytime before your trial ends to avoid a charge.')} <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">{t('trial.terms', 'Terms')}</a> · <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">{t('trial.privacy', 'Privacy')}</a>.</p> : <p dangerouslySetInnerHTML={{ __html: fineHtml }} />}
              </div>}
            </form>
          </aside>

        </div>
      </div>

      {!postingSelected && <div className="tcc-foot">
        <span className="msg">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg>
          <span {...H('footer')} />
        </span>
      </div>}
    </div>
  );
}

export default TrialCardCapture;
